import { useEffect, useState } from "react";
import { api, formatApiError, resolveImage } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = {
  title_en: "",
  title_es: "",
  description_en: "",
  description_es: "",
  category: "carpentry",
  subcategory: "",
  location: "",
  year: new Date().getFullYear(),
  cover_image: "",
  images: [],
  featured: false,
};

export default function AdminProjects() {
  const { t, pick } = useLang();
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/projects").then(({ data }) => setProjects(data));

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };

  const startEdit = (p) => {
    setForm({ ...EMPTY, ...p });
    setEditing(p.id);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        year: Number(form.year) || null,
        images: form.cover_image ? [form.cover_image] : [],
      };
      if (editing === "new") {
        await api.post("/admin/projects", payload);
        toast.success("Project created");
      } else {
        await api.put(`/admin/projects/${editing}`, payload);
        toast.success("Project updated");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Deleted");
    await load();
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-ink">{t("admin.projects")}</h1>
        <button
          data-testid="admin-new-project"
          onClick={startNew}
          className="inline-flex items-center gap-2 px-5 py-3 bg-brand text-ink-inverse text-sm hover:bg-brand-hover transition-colors"
        >
          <Plus size={16} /> {t("admin.addProject")}
        </button>
      </div>

      {editing && (
        <form
          onSubmit={save}
          data-testid="admin-project-form"
          className="bg-bg-surface border border-edge p-6 mb-10 grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          <Input label="Title (EN)" value={form.title_en} onChange={(v) => setForm({ ...form, title_en: v })} required testId="proj-title-en" />
          <Input label="Title (ES)" value={form.title_es} onChange={(v) => setForm({ ...form, title_es: v })} required testId="proj-title-es" />
          <Textarea label="Description (EN)" value={form.description_en} onChange={(v) => setForm({ ...form, description_en: v })} testId="proj-desc-en" />
          <Textarea label="Description (ES)" value={form.description_es} onChange={(v) => setForm({ ...form, description_es: v })} testId="proj-desc-es" />
          <div>
            <label className="text-eyebrow text-ink-muted block mb-2">Category</label>
            <select
              data-testid="proj-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
            >
              <option value="carpentry">Carpentry</option>
              <option value="construction">Construction</option>
              <option value="consultation">Consultation / Design</option>
            </select>
          </div>
          <Input label="Subcategory" value={form.subcategory} onChange={(v) => setForm({ ...form, subcategory: v })} testId="proj-sub" />
          <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="proj-loc" />
          <Input label="Year" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testId="proj-year" />
          <div className="md:col-span-2">
            <ImageUpload
              label="Cover image"
              value={form.cover_image}
              onChange={(v) => setForm({ ...form, cover_image: v })}
              testId="proj-cover"
              required
            />
          </div>
          <label className="flex items-center gap-3 text-ink">
            <input
              data-testid="proj-featured"
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              className="accent-brand w-4 h-4"
            />
            <span className="text-sm">Featured on homepage</span>
          </label>
          <div className="md:col-span-2 flex gap-3 pt-2">
            <button type="submit" data-testid="proj-save" className="px-6 py-3 bg-brand text-ink-inverse text-sm hover:bg-brand-hover">
              {t("admin.save")}
            </button>
            <button type="button" onClick={() => setEditing(null)} className="px-6 py-3 border border-edge text-ink hover:border-brand text-sm">
              {t("admin.cancel")}
            </button>
          </div>
        </form>
      )}

      <div className="bg-bg-surface border border-edge">
        <table className="w-full text-left">
          <thead className="border-b border-edge">
            <tr className="text-eyebrow text-ink-muted">
              <th className="px-5 py-4">Title</th>
              <th className="px-5 py-4 hidden md:table-cell">Category</th>
              <th className="px-5 py-4 hidden lg:table-cell">Year</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id} className="border-b border-edge last:border-0 hover:bg-bg-hover transition-colors">
                <td className="px-5 py-4 text-ink">
                  <div className="flex items-center gap-3">
                    <img src={resolveImage(p.cover_image)} alt="" className="w-12 h-12 object-cover" />
                    <span className="font-display">{pick(p, "title")}</span>
                  </div>
                </td>
                <td className="px-5 py-4 hidden md:table-cell text-ink-muted">{p.category}</td>
                <td className="px-5 py-4 hidden lg:table-cell text-ink-muted">{p.year}</td>
                <td className="px-5 py-4 text-right">
                  <button onClick={() => startEdit(p)} data-testid={`edit-${p.id}`} className="text-ink-muted hover:text-brand mr-4">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-${p.id}`} className="text-ink-muted hover:text-bad">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-eyebrow text-ink-muted block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value ?? ""}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
      />
    </div>
  );
}

function Textarea({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-eyebrow text-ink-muted block mb-2">{label}</label>
      <textarea
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="w-full bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
      />
    </div>
  );
}
